import { useState } from "react";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { saveQcChecklistCategory, saveQcChecklistItem } from "@/lib/manufacturing/builderAssistantChecklistClient";
import { BuilderAssistantChecklistImport } from "./BuilderAssistantChecklistImport";

type NewKind = "category" | "item" | "import";

const CHOICES: { id: NewKind; label: string }[] = [
  { id: "category", label: "Category" },
  { id: "item", label: "Item" },
  { id: "import", label: "Import rows" },
];

export function BuilderAssistantChecklistNew({
  categories,
  onSaved,
}: {
  categories: { id: string; name: string }[];
  onSaved: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<NewKind | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  function close() {
    setOpen(false);
    setKind(null);
    setError(null);
  }

  async function run(task: Promise<{ ok: boolean; error?: string }>) {
    setError(null);
    const res = await task;
    if (!res.ok) {
      setError(res.error ?? "Could not update the checklist.");
      return;
    }
    await onSaved();
    close();
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        className={mfgPrimaryBtnClass}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {open ? "Close" : "+ New"}
      </button>
      {open ? (
        <div className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="What to add">
            {CHOICES.map((choice) => (
              <button
                key={choice.id}
                type="button"
                role="tab"
                aria-selected={kind === choice.id}
                className={
                  kind === choice.id
                    ? "rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900"
                    : ghostBtnClass
                }
                onClick={() => {
                  setError(null);
                  setKind(choice.id);
                }}
              >
                {choice.label}
              </button>
            ))}
          </div>
          {error ? <p className="text-red-700 dark:text-red-300">{error}</p> : null}
          {kind === "category" ? (
            <form
              className="flex flex-wrap gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                void run(saveQcChecklistCategory(categoryName).then((res) => {
                  if (res.ok) setCategoryName("");
                  return res;
                }));
              }}
            >
              <input className={inputClass} value={categoryName} placeholder="New category" onChange={(event) => setCategoryName(event.target.value)} />
              <button type="submit" className={ghostBtnClass}>Add category</button>
            </form>
          ) : null}
          {kind === "item" ? (
            <form
              className="grid gap-2 sm:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                void run(saveQcChecklistItem({ category_id: categoryId, title, description }).then((res) => {
                  if (res.ok) {
                    setTitle("");
                    setDescription("");
                  }
                  return res;
                }));
              }}
            >
              <select className={inputClass} value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
                <option value="">Category</option>
                {categories.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </select>
              <input className={inputClass} value={title} placeholder="Item" onChange={(event) => setTitle(event.target.value)} />
              <input className={`${inputClass} sm:col-span-2`} value={description} placeholder="Description" onChange={(event) => setDescription(event.target.value)} />
              <button type="submit" className={ghostBtnClass}>Add item</button>
            </form>
          ) : null}
          {kind === "import" ? (
            <BuilderAssistantChecklistImport
              existingCategories={categories.map((row) => row.name)}
              onImported={async () => {
                await onSaved();
                close();
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
